import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Dropdown,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQDeleteCategoryAPI,
  RQGetCategoryAPI,
  RQUpdateCategoryAPI,
} from '../../../../src/reactQuery/apis/rqGetProductCategory';
import {selectAuth} from '../../../../src/redux/selectors/authSelector';
import SearchBar from '../../../commonComponents/SeachBar/SearchBar';

import CategoryModal from './CategoryModal/CategoryModal';

const {Option} = Select;

const ProductCategoryScreen = () => {
  const token = useSelector(selectAuth)?.tokenData;

  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState('');

  const {data = [], isLoading, refetch} = RQGetCategoryAPI(token);

  const {mutate: updateCategory} = RQUpdateCategoryAPI({
    onSuccess: () => {
      toast.success('Category updated');
      refetch();
    },
  });

  const {mutate: deleteCategory} = RQDeleteCategoryAPI({
    onSuccess: () => {
      toast.success('Category deleted');
      refetch();
    },
  });

  const handleStatusChange = (record, status) => {
    updateCategory({
      token,
      id: record.id,
      formData: {
        name: record.name,
        hsnCode: record.tax.hsnCode,
        cgst: record.tax.cgst,
        sgst: record.tax.sgst,
        igst: record.tax.igst,
        isActive: status === 'ACTIVE',
        features: {
          features: record.features?.features || [],
        },
      },
    });
  };

  const handleSearch = (text = '') => {
    setSearchText(text); // keep original value
  };
  
  const filteredData = data.filter(cat => {
    if (statusFilter === 'ACTIVE' && !cat.isActive) {return false;}
    if (statusFilter === 'INACTIVE' && cat.isActive) {return false;}

    if (searchText) {
      const search = searchText.trim().toLowerCase();

      const inName = cat.name.toLowerCase().includes(search);
      const inFeatures = cat.features?.features?.some(f =>
        f.toLowerCase().includes(search),
      );

      return inName || inFeatures;
    }

    return true;
  });

  const columns = [
    {title: 'Sr', render: (_, __, i) => i + 1, width: 60},
    {title: 'Name', dataIndex: 'name'},
    {title: 'HSN', render: r => r.tax?.hsnCode || '-'},
    {title: 'CGST %', render: r => r.tax?.cgst ?? '-'},
    {title: 'SGST %', render: r => r.tax?.sgst ?? '-'},
    {title: 'IGST %', render: r => r.tax?.igst ?? '-'},
    {
      title: 'Features',
      render: r =>
        r.features?.features?.length
          ? r.features.features.map(f => (
            <Tag key={f} color={'blue'}>
              {f}
            </Tag>
          ))
          : '-',
    },
    {
      title: 'Status',
      render: r => (
        <Dropdown
          menu={{
            onClick: e => handleStatusChange(r, e.key),
            items: [
              !r.isActive && {key: 'ACTIVE', label: 'Active'},
              r.isActive && {key: 'INACTIVE', label: 'Inactive'},
            ].filter(Boolean),
          }}
        >
          <span className={'cursor-pointer font-medium'}>
            {r.isActive ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </Dropdown>
      ),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <div className={'flex gap-6'}>
          <EditOutlined
            onClick={() => {
              setEditData(record);
              setOpen(true);
            }}
          />
          <Tooltip title={'Delete category'}>
            <Popconfirm
              title={'Are you sure you want to delete this category?'}
              onConfirm={() => deleteCategory({token, id: record.id})}
              okText={'Yes'}
              cancelText={'No'}
            >
              <DeleteOutlined
                className={'text-red-500 cursor-pointer'}
                onClick={() => deleteCategory(record.id)}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <Breadcrumb items={[{title: 'Home'}, {title: 'Category'}]} />

      <div className={'flex justify-between mt-4'}>
        <h1 className={'font-bold text-lg'}>Categories</h1>
        
      </div>

      <div className={'flex gap-2 mt-4'}>
        <SearchBar
          value={searchText}
          allowClear
          onSearch={handleSearch}
          placeholder={'Search categories...'}
        />
        <Select
          allowClear
          placeholder={'Status'}
          className={'w-40'}
          value={statusFilter}
          onChange={setStatusFilter}
        >
          <Option value={'ACTIVE'}>Active</Option>
          <Option value={'INACTIVE'}>Inactive</Option>
        </Select>

        <Button 
          onClick={() => {
            setStatusFilter(null);
            setSearchText(''); 
          }}
        >
          <ReloadOutlined /> Reset
        </Button>
      </div>

      <div className={'mt-4'}>
        <Button
          type={'primary'}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          <PlusOutlined /> Add Category
        </Button>
      </div>

      <div className={'mt-4 overflow-x-auto'}>
        <Table
          rowKey={'id'}
          loading={isLoading}
          columns={columns}
          dataSource={filteredData}
          pagination={{pageSize: 10}}
          className={'mt-4'}
        /></div>

      <CategoryModal
        open={open}
        onCancel={() => setOpen(false)}
        refetch={refetch}
        updateCategoryData={editData}
      />
    </>
  );
};

export default ProductCategoryScreen;
